type TokenMap = Record<string, string | number>;

type TokenDef = {
  cssPrefix: string;
  values: TokenMap;
};

const baseMultiplier = 0.25;

const pxToRem = (px: number) => `${px / 16}rem`;

const fontFamily: TokenDef = {
  cssPrefix: "font-family",
  values: {
    base: `"Macan", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`,
  },
} as const;

type FontFamily = keyof typeof fontFamily.values;

const fontWeight = {
  cssPrefix: "font-weight",
  values: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const;

type FontWeight = keyof typeof fontWeight.values;

const fontSize = {
  cssPrefix: "font-size",
  values: {
    "2xs": pxToRem(10),
    xs: pxToRem(11),
    sm: pxToRem(13),
    md: pxToRem(15),
    lg: pxToRem(17),
    xl: pxToRem(19),
    "2xl": pxToRem(23),
  },
} as const;

type FontSize = keyof typeof fontSize.values;

const lineHeight = {
  cssPrefix: "line-height",
  values: {
    "2xs": pxToRem(12),
    xs: pxToRem(16),
    sm: pxToRem(20),
    md: pxToRem(24),
    lg: pxToRem(28),
    xl: pxToRem(32),
    "2xl": pxToRem(36),
  },
} as const;

type LineHeight = keyof typeof lineHeight.values;

const spacing = {
  cssPrefix: "spacing",
  values: {
    none: 0,
    "0": 0, 
    "0-5": `${baseMultiplier * 0.5}rem`,
    "1": `${baseMultiplier * 1}rem`,
    "1-5": `${baseMultiplier * 1.5}rem`,
    "2": `${baseMultiplier * 2}rem`,
    "2-5": `${baseMultiplier * 2.5}rem`,
    "3": `${baseMultiplier * 3}rem`,
    "4": `${baseMultiplier * 4}rem`,
    "5": `${baseMultiplier * 5}rem`,
    "6": `${baseMultiplier * 6}rem`,
    "8": `${baseMultiplier * 8}rem`,
    "10": `${baseMultiplier * 10}rem`,
  },
} as const;

type Spacing = keyof typeof spacing.values;

const radius = {
  cssPrefix: "radius",
  values: {
    none: 0,
    "0-5": `${baseMultiplier * 0.5}rem`,
    "1": `${baseMultiplier * 1}rem`,
    "1-5": `${baseMultiplier * 1.5}rem`,
    "2": `${baseMultiplier * 2}rem`,
    "2-5": `${baseMultiplier * 2.5}rem`,
    "3": `${baseMultiplier * 3}rem`,
    "3-5": `${baseMultiplier * 3.5}rem`,
    "4": `${baseMultiplier * 4}rem`,
    "5": `${baseMultiplier * 5}rem`,
    "6": `${baseMultiplier * 6}rem`,
    max: `999rem`,
  },
} as const;

type Radius = keyof typeof radius.values;

const size = {
  cssPrefix: "size",
  values: {
    s50: `${baseMultiplier * 1}rem`,
    s100: `${baseMultiplier * 3}rem`,
    s200: `${baseMultiplier * 4}rem`,
    s300: `${baseMultiplier * 5}rem`,
    s400: `${baseMultiplier * 6}rem`,
    s500: `${baseMultiplier * 7}rem`,
    s600: `${baseMultiplier * 8}rem`,
    s700: `${baseMultiplier * 9}rem`,
    s800: `${baseMultiplier * 10}rem`,
    s900: `${baseMultiplier * 11}rem`,
    s1000: `${baseMultiplier * 12}rem`,
    s1100: `${baseMultiplier * 16}rem`,
    s1200: `${baseMultiplier * 20}rem`,
  },
} as const;

type Size = keyof typeof size.values;

const color = {
  cssPrefix: "color",
  values: {
    // Base colors
    white: "hsla(0, 0%, 100%, 1)",
    black: "hsla(0, 0%, 0%, 1)",
    "ping-orange": "#F32C01",

    // Neutral colors
    "neutral-50": "#F9FAFC",
    "neutral-100": "#EEF2F8",
    "neutral-200": "#DFE5EF",
    "neutral-300": "#CDD5DD",
    "neutral-400": "#BEC4CC",
    "neutral-500": "#9298A0",
    "neutral-600": "#666C77",
    "neutral-700": "#32363D",
    "neutral-800": "#1E1F36",
    "neutral-900": "#0E0F1D",

    // Brand colors
    "brand-50": "#FFF2EF",
    "brand-100": "#FED2C6",
    "brand-200": "#FDAC94",
    "brand-300": "#FC7957",
    "brand-400": "#FB5529",
    "brand-500": "#DE3600",
    "brand-600": "#C72900",
    "brand-700": "#961105",
    "brand-800": "#720907",
    "brand-900": "#300203",

    // Success colors
    "success-50": "#DCFAEE",
    "success-100": "#BEF6DF",
    "success-200": "#76DAA9",
    "success-300": "#4ABB80",
    "success-400": "#36AF70",
    "success-500": "#109955",
    "success-600": "#00835C",
    "success-700": "#0D5B25",
    "success-800": "#0F451B",
    "success-900": "#061B07",

    // Warning colors
    "warning-50": "#FFF7EC",
    "warning-100": "#FFD79A",
    "warning-200": "#FDB51C",
    "warning-300": "#D89B04",
    "warning-400": "#C48D02",
    "warning-500": "#A57700",
    "warning-600": "#926900",
    "warning-700": "#644714",
    "warning-800": "#552C03",
    "warning-900": "#211300",

    // Danger colors
    "danger-50": "#FFF2F5",
    "danger-100": "#FFD0D4",
    "danger-200": "#FEA6B1",
    "danger-300": "#FB6E8A",
    "danger-400": "#F74775",
    "danger-500": "#D9255F",
    "danger-600": "#C60053",
    "danger-700": "#91003E",
    "danger-800": "#6E002D",
    "danger-900": "#2F000E",

    // Secondary colors
    "secondary-50": "#E3EFFF",
    "secondary-100": "#BFE2FA",
    "secondary-200": "#8AC8F7",
    "secondary-300": "#48A7F3",
    "secondary-400": "#0B92F0",
    "secondary-500": "#0074D4",
    "secondary-600": "#0065BC",
    "secondary-700": "#00488F",
    "secondary-800": "#043769",
    "secondary-900": "#00142D",

    // Translucent colors
    "translucent-200": "hsla(230, 33%, 4%, 0.23)",
    "translucent-400": "hsla(230, 33%, 4%, 0.43)",
    "translucent-700": "hsla(230, 33%, 4%, 0.72)",
    "translucent-800": "hsla(230, 33%, 4%, 0.83)",
    "translucent-900": "hsla(230, 33%, 4%, 0.91)",

    // Translucent Light colors
    "translucent-light-600": "hsla(0, 0%, 100%, 0.6)",
    "translucent-light-800": "hsla(0, 0%, 100%, 0.8)",
  },
} as const;

type Color = keyof typeof color.values;

const iconStroke = {
  cssPrefix: "icon-stroke",
  values: {
    "i0-75": 0.75,
    i1: 1,
    "i1-25": 1.25,
    "i1-5": 1.5,
    "i1-75": 1.75,
    i2: 2,
  },
} as const;

type IconStroke = keyof typeof iconStroke.values;

const borderThickness = {
  cssPrefix: "border-thickness",
  values: {
    none: 0,
    "1px": "1px",
    "2px": "2px",
    "1": `${baseMultiplier}rem`,
  },
} as const;

type BorderThickness = keyof typeof borderThickness.values;

const border = {
  cssPrefix: "border",
  values: {
    "neutral-100":
      "solid var(--ping-border-thickness-1px) var(--ping-color-neutral-100)",
    dashed:
      "dashed var(--ping-border-thickness-1px) var(--ping-color-neutral-300)",
  },
} as const;

type Border = keyof typeof border.values;

const boxShadow = {
  cssPrefix: "box-shadow",
  values: {
    none: "none",
    panel: "0 4px 8px rgba(0, 0, 0, 0.06), 0 0 4px rgba(0, 0, 0, 0.04)",
    subtle: "0 1px 1px rgba(0, 0, 0, 0.03), 0 0 1px rgba(0, 0, 0, 0.05)",
    "subtle-noblur":
      "0 0 0 1px rgba(0, 0, 0, 0.06), 0 1px 2px -1px rgba(0, 0, 0, 0.06), 0 2px 4px 0 rgba(0, 0, 0, 0.04)",
    fab: "0 1px 2px rgba(0, 0, 0, 0.3), 0 2px 6px 2px rgba(0, 0, 0, 0.15)",
    tooltip:
      "inset 0 0 2px rgba(0, 0, 0, 0.25), 0 12px 5px rgba(0, 0, 0, 0.01), 0 7px 4px rgba(0, 0, 0, 0.05), 0 3px 3px rgba(0, 0, 0, 0.09), 0 1px 2px rgba(0, 0, 0, 0.1)",
    modal: "0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08)",
  },
} as const;
type Shadow = keyof typeof boxShadow.values;

const backdropFilter = {
  cssPrefix: "backdrop-filter",
  values: {
    surface: "blur(25px)",
  },
} as const;

type BackdropFilter = keyof typeof backdropFilter.values;

const designTokens = [
  color,
  fontFamily,
  fontWeight,
  fontSize,
  lineHeight,
  spacing,
  radius,
  size,
  iconStroke,
  borderThickness,
  border,
  boxShadow,
  backdropFilter,
];

export {
  type TokenMap,
  type TokenDef,
  type FontFamily,
  type FontWeight,
  type FontSize,
  type LineHeight,
  spacing,
  type Spacing,
  radius,
  type Radius,
  size,
  type Size,
  type Color,
  type IconStroke,
  type BorderThickness,
  type Border,
  boxShadow,
  type Shadow,
  designTokens,
  type BackdropFilter,
  backdropFilter,
};
